// Shared helpers for the Smart Objects param form (SmartObjectsSidebar and
// its field components). Pure functions only — no React.
//
// FillValue contract (posterTools/types.ts + posterTools/fill.ts):
//   plain string (legacy solid)
//   | { type: 'solid', color }
//   | { type: 'linear-gradient', from, to, angle? }
//   | { type: 'texture', url, scale? }

/** Normalize any FillValue (incl. legacy plain strings) to the object shape. */
export function normalizeFill(value) {
  if (typeof value === 'string' && value.trim()) {
    return { type: 'solid', color: value };
  }
  if (value && typeof value === 'object') {
    if (value.type === 'linear-gradient') {
      const angle = Number(value.angle);
      return {
        type: 'linear-gradient',
        from: value.from || '#3b82f6',
        to: value.to || '#8b5cf6',
        angle: Number.isFinite(angle) ? angle : 90,
      };
    }
    if (value.type === 'texture') {
      const scale = Number(value.scale);
      return {
        type: 'texture',
        url: value.url || '',
        scale: Number.isFinite(scale) && scale > 0 ? scale : 1,
      };
    }
    if (value.type === 'solid') {
      return { type: 'solid', color: value.color || '#cccccc' };
    }
  }
  return { type: 'solid', color: '#cccccc' };
}

/** CSS `background` shorthand previewing a FillValue (swatches, strips). */
export function fillPreviewCss(value) {
  const fill = normalizeFill(value);
  if (fill.type === 'linear-gradient') {
    // Canvas convention (fill.ts): 0 = left-to-right. CSS: 0deg = bottom-to-top,
    // 90deg = left-to-right — offset so the preview matches the canvas.
    return `linear-gradient(${(Number(fill.angle) || 0) + 90}deg, ${fill.from}, ${fill.to})`;
  }
  if (fill.type === 'texture') {
    return fill.url
      ? `#0f172a center / cover no-repeat url("${fill.url}")`
      // checkerboard placeholder while no URL is set
      : 'repeating-conic-gradient(#334155 0% 25%, #475569 0% 50%) 0 0 / 12px 12px';
  }
  return fill.color;
}

export function fillModeLabel(value) {
  const fill = normalizeFill(value);
  if (fill.type === 'linear-gradient') return 'Gradient';
  if (fill.type === 'texture') return 'Texture';
  return `Solid ${fill.color}`;
}

/** <input type="color"> only accepts #rrggbb — anything else falls back. */
export function safeHexInput(value, fallback = '#000000') {
  return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback;
}

/**
 * Value-equality with the small type tolerances the draft actually produces:
 * a number-typed field emits its value as a string ('2'), a checkbox emits a
 * real boolean but a preset may seed 'true'/'false'. So equals:2 matches a '2'
 * draft and equals:true matches a 'true' draft, while plain strings/selects
 * stay strict. Anything else is a mismatch.
 */
export function draftValueEquals(actual, expected) {
  if (actual === expected) return true;
  if (typeof expected === 'number') {
    const n = typeof actual === 'string' && actual.trim() !== '' ? Number(actual) : actual;
    return n === expected;
  }
  if (typeof expected === 'boolean') {
    if (actual === 'true') return expected === true;
    if (actual === 'false') return expected === false;
    return false;
  }
  return false;
}

/**
 * Evaluate a ParamField's `visibleWhen` (posterTools/types.ts) against a bag of
 * current values — the modal draft for top-level fields, a list row's own
 * values for itemSchema sub-fields. Exactly one condition (equals | notEquals);
 * absent or malformed → visible. Hidden fields are simply not rendered; their
 * draft values are left untouched, so generate() still receives every param.
 */
export function isFieldVisible(field, values) {
  const cond = field && field.visibleWhen;
  if (!cond || typeof cond !== 'object' || typeof cond.key !== 'string') return true;
  const actual = values ? values[cond.key] : undefined;
  if ('equals' in cond) return draftValueEquals(actual, cond.equals);
  if ('notEquals' in cond) return !draftValueEquals(actual, cond.notEquals);
  return true;
}

/** Sensible empty value for one scalar list item (itemType lists). */
export function defaultScalarItem(field) {
  if (field.itemType === 'number') return Number.isFinite(field.min) ? field.min : 0;
  if (field.itemType === 'color') return '#cccccc';
  return '';
}

/** Sensible empty value for one ParamField inside a list itemSchema. */
export function defaultForItemField(field) {
  switch (field.type) {
    case 'number': return Number.isFinite(field.min) ? field.min : 0;
    case 'color': return '#cccccc';
    case 'boolean': return false;
    case 'select': return field.options?.[0]?.value ?? '';
    case 'fill': return '#cccccc';
    default: return ''; // text / multiline / font / image
  }
}

/** New list item derived from the itemSchema (object) or itemType (scalar). */
export function defaultListItem(field) {
  if (field.itemType) return defaultScalarItem(field);
  const schema = field.itemSchema || [];
  // All-numeric keys ("0","1",...) mean the stored item is an ARRAY addressed
  // by index (e.g. data-table rows are string[][]); synthesizing an object
  // here would be silently dropped by the recipe's Array.isArray guard.
  if (schema.length && schema.every((sub) => /^\d+$/.test(sub.key))) {
    const arr = [];
    for (const sub of schema) arr[Number(sub.key)] = defaultForItemField(sub);
    return arr;
  }
  const item = {};
  for (const sub of schema) item[sub.key] = defaultForItemField(sub);
  return item;
}

/**
 * Coerce one scalar draft value to its param type. Throws a user-readable
 * Error on bad input (label is the field label, optionally row-prefixed).
 */
export function coerceScalar(type, value, label) {
  if (type === 'number') {
    const n = Number(value);
    if (value === '' || value === null || value === undefined || !Number.isFinite(n)) {
      throw new Error(`"${label}" must be a number.`);
    }
    return n;
  }
  if (type === 'boolean') return !!value;
  return value;
}

/**
 * Coerce a whole list draft value: enforces min/maxItems and per-item types
 * (itemType scalars, or itemSchema fields per row). Throws readable Errors.
 */
export function coerceList(field, value) {
  const items = Array.isArray(value) ? value : [];
  if (Number.isFinite(field.minItems) && items.length < field.minItems) {
    throw new Error(`"${field.label}" needs at least ${field.minItems} item${field.minItems === 1 ? '' : 's'}.`);
  }
  if (Number.isFinite(field.maxItems) && items.length > field.maxItems) {
    throw new Error(`"${field.label}" allows at most ${field.maxItems} item${field.maxItems === 1 ? '' : 's'}.`);
  }
  if (field.itemType) {
    return items.map((item, i) => coerceScalar(field.itemType, item, `${field.label} #${i + 1}`));
  }
  return items.map((item, i) => {
    // ARRAY items (numeric-key itemSchemas, e.g. data-table rows) must stay
    // arrays — `{ ...item }` would turn ['a','b'] into {0:'a',1:'b'} and the
    // recipe's Array.isArray guard would silently drop the row.
    const row = Array.isArray(item) ? [...item] : { ...item };
    for (const sub of field.itemSchema || []) {
      if (sub.type === 'list') continue; // nested lists pass through as-is
      row[sub.key] = coerceScalar(sub.type, row[sub.key], `${field.label} #${i + 1} — ${sub.label}`);
    }
    return row;
  });
}

/** Title + subtitle for a collapsed list row (summaryKeys; first = title). */
export function listRowSummary(field, item, index) {
  if (field.itemType) {
    const text = item === '' || item === null || item === undefined ? `Item ${index + 1}` : String(item);
    return { title: text, subtitle: '' };
  }
  const keys = field.summaryKeys?.length
    ? field.summaryKeys
    : (field.itemSchema || []).slice(0, 2).map((f) => f.key);
  const raw = (k) => {
    const v = item?.[k];
    if (v === undefined || v === null || v === '') return '';
    if (typeof v === 'object') return fillModeLabel(v); // fills in summaries
    return String(v);
  };
  const title = raw(keys[0]) || `Item ${index + 1}`;
  const subtitle = keys.slice(1).map(raw).filter(Boolean).join(' · ');
  return { title, subtitle };
}

// ---------------------------------------------------------------------------
// Draft machinery (moved out of SmartObjectsSidebar when the edit form became
// the SmartToolModal, 2026-07-23). A DRAFT is the form's working copy of a
// recipe's params: lists/fills deep-cloned so row edits never mutate recipe
// defaults or the live object's params; legacy json params become pretty-
// printed strings parsed back on Apply.
// ---------------------------------------------------------------------------

export function deepCloneValue(v) {
  try {
    return JSON.parse(JSON.stringify(v));
  } catch (_) {
    return v;
  }
}

/** Seed a draft from params merged over the recipe defaults. */
export function seedDraft(recipe, params) {
  const merged = { ...recipe.defaults, ...params };
  const draft = {};
  for (const field of recipe.schema) {
    const value = merged[field.key];
    if (field.type === 'json') {
      draft[field.key] = value === undefined ? '' : JSON.stringify(value, null, 2);
    } else if (field.type === 'boolean') {
      draft[field.key] = !!value;
    } else if (field.type === 'list') {
      draft[field.key] = Array.isArray(value) ? deepCloneValue(value) : [];
    } else if (field.type === 'fill') {
      draft[field.key] = typeof value === 'object' && value !== null ? deepCloneValue(value) : value;
    } else {
      draft[field.key] = value === undefined || value === null ? '' : value;
    }
  }
  return draft;
}

/**
 * Merge a grade-band preset (ToolRecipe.gradeBands[bandId] — a partial params
 * patch) into an existing draft, shaping each value exactly like seedDraft.
 * Keys the schema doesn't know are ignored (presets must stay honest).
 */
export function mergeBandPreset(recipe, draft, preset) {
  if (!preset || typeof preset !== 'object') return draft;
  const next = { ...draft };
  for (const field of recipe.schema) {
    if (!(field.key in preset)) continue;
    const value = preset[field.key];
    if (field.type === 'json') {
      next[field.key] = value === undefined ? '' : JSON.stringify(value, null, 2);
    } else if (field.type === 'boolean') {
      next[field.key] = !!value;
    } else if (field.type === 'list') {
      next[field.key] = Array.isArray(value) ? deepCloneValue(value) : next[field.key];
    } else if (field.type === 'fill') {
      next[field.key] = typeof value === 'object' && value !== null ? deepCloneValue(value) : value;
    } else {
      next[field.key] = value === undefined || value === null ? '' : value;
    }
  }
  return next;
}

/**
 * Section-grouped view of the NON-advanced fields. Fields WITHOUT a `section`
 * render first in schema order, then one headed group per section in order of
 * first appearance (fields inside keep schema order). `sectionOrder` (the
 * recipe's optional modal spec) promotes named sections to the front, in that
 * order; unlisted sections keep their first-appearance order after them.
 */
export function groupBySection(fields, sectionOrder) {
  const ungrouped = [];
  const groups = [];
  const byName = new Map();
  for (const field of fields) {
    const section = typeof field.section === 'string' ? field.section.trim() : '';
    if (!section) {
      ungrouped.push(field);
      continue;
    }
    let group = byName.get(section);
    if (!group) {
      group = { section, fields: [] };
      byName.set(section, group);
      groups.push(group);
    }
    group.fields.push(field);
  }
  if (Array.isArray(sectionOrder) && sectionOrder.length > 0) {
    const rank = new Map(sectionOrder.map((name, i) => [name, i]));
    // Stable: named sections first by their sectionOrder rank, the rest keep
    // first-appearance order.
    groups.sort((a, b) => {
      const ra = rank.has(a.section) ? rank.get(a.section) : Infinity;
      const rb = rank.has(b.section) ? rank.get(b.section) : Infinity;
      return ra - rb;
    });
  }
  return { ungrouped, groups };
}

/**
 * Draft → params for generate/regenerate. Throws a user-readable Error on bad
 * input (non-numeric number field, short list, invalid legacy JSON) — callers
 * toast it and bail.
 */
export function buildParamsFromDraft(recipe, draft) {
  const params = {};
  for (const field of recipe.schema) {
    const value = draft[field.key];
    if (field.type === 'list') {
      params[field.key] = coerceList(field, value);
    } else if (field.type === 'fill') {
      // FillValue passes through as-is (object shapes or legacy string);
      // the recipe resolves it via posterTools' applyFill/resolveFill.
      params[field.key] = value;
    } else if (field.type === 'json') { // legacy fallback path only
      const text = String(value ?? '').trim();
      if (!text) continue; // empty = keep the recipe default
      try {
        params[field.key] = JSON.parse(text);
      } catch (_) {
        throw new Error(`"${field.label}" is not valid JSON.`);
      }
    } else {
      params[field.key] = coerceScalar(field.type, value, field.label);
    }
  }
  return params;
}

/**
 * Every font family referenced by the current draft — top-level `font`
 * fields plus `font` fields inside list itemSchemas. Used to guarantee the
 * faces are loaded before Apply regenerates (text must measure correctly).
 */
export function collectDraftFontFamilies(schema, draft) {
  const families = new Set();
  for (const field of schema) {
    if (field.type === 'font') {
      const v = draft[field.key];
      if (typeof v === 'string' && v.trim()) families.add(v.trim());
    } else if (field.type === 'list' && !field.itemType && Array.isArray(draft[field.key])) {
      const fontSubs = (field.itemSchema || []).filter((s) => s.type === 'font');
      for (const item of draft[field.key]) {
        for (const sub of fontSubs) {
          const v = item?.[sub.key];
          if (typeof v === 'string' && v.trim()) families.add(v.trim());
        }
      }
    }
  }
  return [...families];
}
